import { useState, useEffect } from 'react';
import { useI18n } from '../i18n';

export interface WattelzceFilterValues {
  startDate: string;
  endDate: string;
  deviceName: string;
  deviceVendor: string;
}

interface WattelzceFilterSidebarProps {
  onFilterChange: (filters: WattelzceFilterValues) => void;
  deviceNameOptions?: Array<{ deviceId: number; deviceName: string }>;
  deviceVendorOptions?: Array<{ vendor: string }>;
}

function WattelzceFilterSidebar({ 
  onFilterChange, 
  deviceNameOptions = [],
  deviceVendorOptions = [] 
}: WattelzceFilterSidebarProps) {
  const { t } = useI18n();
  // Default to last 30 days
  const getDefaultDates = () => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);
    return {
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0],
    };
  };

  const defaultDates = getDefaultDates();
  
  const [filters, setFilters] = useState<WattelzceFilterValues>({
    startDate: defaultDates.startDate,
    endDate: defaultDates.endDate,
    deviceName: '',
    deviceVendor: '',
  });

  // Trigger initial filter on mount
  useEffect(() => {
    onFilterChange(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (field: keyof WattelzceFilterValues, value: string) => {
    const newFilters = { ...filters, [field]: value };
    setFilters(newFilters);
  };

  const handleApply = () => {
    onFilterChange(filters);
  };

  const handleReset = () => {
    const resetFilters = {
      ...defaultDates,
      deviceName: '',
      deviceVendor: '',
    };
    setFilters(resetFilters);
    onFilterChange(resetFilters);
  };

  // Quick date presets
  const setPreset = (days: number) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    const newFilters = {
      ...filters,
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0],
    };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  return (
    <aside className="w-64 min-h-screen bg-emerald-900 text-white p-4 flex flex-col gap-6">
      <div className="border-b border-emerald-700 pb-4">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
          </svg>
          {t('filters')}
        </h2>
      </div>

      {/* Date Filter */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-emerald-200">
          {t('eq2_tf_period')}
        </label>
        
        {/* Quick presets */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setPreset(7)}
            className="px-2 py-1 text-xs bg-emerald-800 hover:bg-emerald-700 rounded transition-colors"
          >
            {t('leg2_preset_7d')}
          </button>
          <button
            onClick={() => setPreset(30)}
            className="px-2 py-1 text-xs bg-emerald-800 hover:bg-emerald-700 rounded transition-colors"
          >
            {t('leg2_preset_30d')}
          </button>
          <button
            onClick={() => setPreset(90)}
            className="px-2 py-1 text-xs bg-emerald-800 hover:bg-emerald-700 rounded transition-colors"
          >
            {t('leg2_preset_90d')}
          </button>
          <button
            onClick={() => setPreset(365)}
            className="px-2 py-1 text-xs bg-emerald-800 hover:bg-emerald-700 rounded transition-colors"
          >
            {t('leg2_preset_1y')}
          </button>
        </div>

        <div className="space-y-2">
          <div>
            <label className="block text-xs text-emerald-300 mb-1">{t('leg2_start_date')}</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleChange('startDate', e.target.value)}
              className="w-full px-3 py-2 bg-emerald-800 border border-emerald-700 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          <div>
            <label className="block text-xs text-emerald-300 mb-1">{t('leg2_end_date')}</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleChange('endDate', e.target.value)}
              className="w-full px-3 py-2 bg-emerald-800 border border-emerald-700 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
        </div>
      </div>

      {/* Device Name Filter */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-emerald-200">
          {t('leg2_device_name')}
        </label>
        <select
          value={filters.deviceName}
          onChange={(e) => handleChange('deviceName', e.target.value)}
          className="w-full px-3 py-2 bg-emerald-800 border border-emerald-700 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
        >
          <option value="">{t('eq_allDevices')}</option>
          {deviceNameOptions.map((device) => (
            <option key={device.deviceId} value={device.deviceName}>
              {device.deviceName}
            </option>
          ))}
        </select>
      </div>

      {/* Device Vendor Filter */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-emerald-200">
          {t('leg2_vendor')}
        </label>
        <select
          value={filters.deviceVendor}
          onChange={(e) => handleChange('deviceVendor', e.target.value)}
          className="w-full px-3 py-2 bg-emerald-800 border border-emerald-700 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
        >
          <option value="">{t('leg2_all_vendors')}</option>
          {deviceVendorOptions.map((vendor) => (
            <option key={vendor.vendor} value={vendor.vendor}>
              {vendor.vendor}
            </option>
          ))}
        </select>
      </div>

      {/* Action Buttons */}
      <div className="mt-auto space-y-2">
        <button
          onClick={handleApply}
          className="w-full px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded font-medium transition-colors flex items-center justify-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          {t('apply')}
        </button>
        <button
          onClick={handleReset}
          className="w-full px-4 py-2 bg-emerald-700 hover:bg-emerald-600 text-white rounded font-medium transition-colors"
        >
          {t('reset')}
        </button>
      </div>
    </aside>
  );
}

export default WattelzceFilterSidebar;
